import { useRef, useEffect, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { v4 as uuidv4 } from 'uuid';
import { useConfigService } from '@thd-nucleus/experience-context';
import { debounce } from '../../../../utils/debounce';
import {
  cabinetToBoundingBox,
  isCornerCabinetByItemId
} from '../utils';
import { useCabinetVisualizerStore } from '../../../../store';
import {
  selectDesignProducts,
  selectDesignAnchorSide,
  selectDesignAccessories,
  DEFAULT_VIEWPORT_WALLSIZE,
  BASE_CABINET_HEIGHT,
  CABINET_GAP_SPACE,
  DEFAULT_TOUCH_POINT_WIDTH
} from '../../../../store/cabinetVisualizer/selectors';
import { getShiftedProducts } from '../WallDesigner/dnd/handleDragAndDrop';
import { getAdjustedCabinetOrigin } from '../WallDesigner/utils/cabinetLayout';
import { CABINET_SKU, DIRECTION } from '../../cabinetConstants';

export const useViewportResize = (zoom = 1) => {
  const [width, setWidth] = useState(0);
  const [zoomedWidth, setZoomedWidth] = useState(0);
  const elementRef = useRef(null);
  const [isRefMounted, setIsRefMounted] = useState(false);

  function setElementRef(elem) {
    elementRef.current = elem;
    if (elem) {
      setIsRefMounted(true);
    } else {
      setIsRefMounted(false);
    }
  }

  const updateWidth = (newZoom) => {
    if (elementRef.current) {
      setWidth(elementRef.current.getBoundingClientRect().width);
      setZoomedWidth(elementRef.current.getBoundingClientRect().width * newZoom);
      elementRef.current.style.zoom = newZoom;
    }
  };

  const updateWidthWithDelay = debounce((newZoom) => updateWidth(newZoom), 300);
  const handleUpdateWidthWithDelay = () => {
    updateWidthWithDelay(zoom);
  };

  useEffect(() => {
    if (isRefMounted) {
      // Get the width on mount
      updateWidth(zoom);

      setTimeout(() => {
        elementRef.current.scrollTop = elementRef.current.scrollHeight;
        elementRef.current.scrollLeft = elementRef.current.scrollWidth / 4;
      });
    }
  }, [isRefMounted]);

  useEffect(() => {
    // Update width on window resize and cleanup
    window.addEventListener('resize', handleUpdateWidthWithDelay);
    return () => {
      window.removeEventListener('resize', handleUpdateWidthWithDelay);
    };
  }, [handleUpdateWidthWithDelay]);

  useEffect(() => {
    updateWidth(zoom);
  }, [zoom]);

  return {
    width,
    zoomedWidth,
    setElementRef
  };
};

export const useSelectedCabinetInfo = () => {
  const [type, setType] = useState('');
  const [color, setColor] = useState('');
  const [height, setHeight] = useState('');
  const [width, setWidth] = useState('');
  const callbackMap = useRef({
    type: setType,
    color: setColor,
    height: setHeight,
    width: setWidth
  });

  return {
    setState: (key, value) => callbackMap.current[key](value),
    info: { type, color, height, width }
  };
};

export const getVoidSpaceWidth = (cabinetType) => {
  let voidSpaceWidth = 0;
  if (['Corner Lazy Susan', 'Corner Standard'].includes(cabinetType)) {
    voidSpaceWidth = 4;
  }
  if (['Corner Blind'].includes(cabinetType)) {
    voidSpaceWidth = 6;
  }
  return voidSpaceWidth;
};

function getVoidSpace(cabinetType, newCabinet, direction, id, productType) {
  const voidSpaceWidth = getVoidSpaceWidth(cabinetType);

  if (voidSpaceWidth === 0) {
    return null;
  }
  const column = direction === DIRECTION.LEFT ? newCabinet.left - voidSpaceWidth : newCabinet.left + newCabinet.width;
  const product = {
    Sku: CABINET_SKU.VOID_SPACE,
    Precedence: 0,
    ChoiceId: null,
    ChoiceBundle: undefined,
    Origin: {
      Row: newCabinet.top,
      Column: column,
      Height: newCabinet.height,
      Width: voidSpaceWidth
    },
    touchPointDirection: direction,
    Name: 'Void Space',
    id,
    productType
  };

  return product;
}

export function useDesignAddTouchCabinet(designId, cabinets, enableTouchPointsOnBothSides) {
  const {
    updateDesignProducts,
    cabinetVisualizer,
  } = useCabinetVisualizerStore();

  const products = cabinets || selectDesignProducts(cabinetVisualizer, designId);
  const anchorSide = selectDesignAnchorSide(cabinetVisualizer, designId);

  const host = useConfigService('global-custom-commerce:host');
  const metadataBaseUrl = useConfigService('graphQL:metadataBaseUrl');
  const { superSkuProducts } = cabinetVisualizer.skuDrawer;

  const defaultMaxColumn = enableTouchPointsOnBothSides
    ? DEFAULT_VIEWPORT_WALLSIZE.Columns - DEFAULT_TOUCH_POINT_WIDTH
    : DEFAULT_VIEWPORT_WALLSIZE.Columns;

  const defaultMinColumn = enableTouchPointsOnBothSides
    ? DEFAULT_TOUCH_POINT_WIDTH
    : 0;

  const getMinAndMaxColumn = (items) => {
    const minColumn = _isEmpty(items)
      ? defaultMinColumn
      : Math.min(...items.map((cabinet) => cabinet.Origin.Column));
    const maxColumn = _isEmpty(items)
      ? defaultMaxColumn
      : Math.max(...items.map((cabinet) => cabinet.Origin.Column + cabinet.Origin.Width));

    const wallProducts = items
      .filter((cabinet) => cabinet.productType.includes('Wall')
      || (cabinet.productType.includes('Base') && cabinet.Origin.Height > BASE_CABINET_HEIGHT + CABINET_GAP_SPACE));

    const baseProducts = items
      .filter((cabinet) => cabinet.productType.includes('Base'));
    const minColumnWall = _isEmpty(wallProducts)
      ? defaultMinColumn
      : Math.min(...wallProducts.map((cabinet) => cabinet.Origin.Column));
    const maxColumnWall = _isEmpty(wallProducts)
      ? defaultMaxColumn
      : Math.max(...wallProducts.map((cabinet) => cabinet.Origin.Column + cabinet.Origin.Width));

    const minColumnBase = _isEmpty(baseProducts)
      ? defaultMinColumn
      : Math.min(...baseProducts.map((cabinet) => cabinet.Origin.Column));
    const maxColumnBase = _isEmpty(baseProducts)
      ? defaultMaxColumn
      : Math.max(...baseProducts.map((cabinet) => cabinet.Origin.Column + cabinet.Origin.Width));

    return {
      minColumn, maxColumn, minColumnWall, maxColumnWall, minColumnBase, maxColumnBase
    };
  };

  const getDirection = (cornerCabinetOrigin, productType, touchPointDirection) => {
    if (_isEmpty(products)) {
      return _isEmpty(anchorSide) ? touchPointDirection : anchorSide;
    }
    if (touchPointDirection) {
      return touchPointDirection;
    }
    const {
      minColumn, maxColumn, minColumnWall, maxColumnWall, minColumnBase, maxColumnBase
    } = getMinAndMaxColumn(products);

    const isWall = productType.includes('Wall');
    if (isWall) {
      if (cornerCabinetOrigin.Column < minColumnWall) {
        return DIRECTION.LEFT;
      }
      if (cornerCabinetOrigin.Column >= maxColumnWall) {
        return DIRECTION.RIGHT;
      }
      return touchPointDirection;
    }
    if (cornerCabinetOrigin.Column < minColumnBase) {
      return DIRECTION.LEFT;
    }
    if (cornerCabinetOrigin.Column >= maxColumnBase) {
      return DIRECTION.RIGHT;
    }
    return touchPointDirection;
  };

  return function addTouchPointCabinet({ boundingBox, cabinetInfo }) {
    const {
      cornerCabinet,
      cornerCabinetType
    } = isCornerCabinetByItemId(String(cabinetInfo.parentId), superSkuProducts);
    const newCabinetIndex = products?.length;
    const newCabinet = {
      ...boundingBox,
      index: newCabinetIndex,
      id: cabinetInfo.id || uuidv4(),
      productType: cabinetInfo.productType,
      cornerCabinet,
      cornerCabinetType,
    };

    const cabinetBoundings = products?.map((cab, index) => {
      const box = cabinetToBoundingBox(cab);
      box.index = index;
      return box;
    });

    // Pull updated cabinets back into a results array
    let updatedProducts = cabinetBoundings.reduce((result, bound) => {
      // Find the original product
      const originalProduct = products.find((pr) => products.indexOf(pr) === bound.index);
      // If we need to check for newCabinet within list later do it here.
      if (originalProduct) {
        // Push it into update list with new position info
        result.push({
          ...originalProduct,
          Origin: {
            Column: bound.left,
            Width: bound.right - bound.left,
            Row: bound.top,
            Height: bound.bottom - bound.top
          }
        });
      }
      return result;
    }, []);

    const newCabinetClone = { ...newCabinet,
      Origin: {
        Row: newCabinet.top,
        Column: newCabinet.left,
        Height: newCabinet.height,
        Width: newCabinet.width
      } };
    const newOrigin = getAdjustedCabinetOrigin({
      productType: cabinetInfo.productType,
      origin: newCabinetClone.Origin,
      cabinet: newCabinetClone,
      products: products || [],
      direction: cabinetInfo.direction,
      cabinetInfo
    });
    const voidSpaceWidth = getVoidSpaceWidth(cornerCabinetType);

    const direction = getDirection(
      newOrigin, cabinetInfo.productType, cabinetInfo.touchPointDirection || cabinetInfo.direction
    );

    const newCabinetBox = {
      left: newOrigin.Column,
      top: newOrigin.Row,
      width: cabinetInfo.width,
      height: cabinetInfo.height,
    };
    // Append the new cabinet after it's been shifted
    if (cornerCabinet && boundingBox.touchPointDirection === DIRECTION.RIGHT) {
      const voidSpacer = getVoidSpace(
        cornerCabinetType, newCabinetBox, direction,
        newCabinet.id, cabinetInfo.productType
      );

      if (voidSpacer) {
        updatedProducts.push(voidSpacer);
      }
    }
    updatedProducts.push({
      Sku: Number(cabinetInfo.itemId),
      Precedence: 0,
      ChoiceId: cabinetInfo.ChoiceId || null,
      ChoiceBundle: undefined,
      Origin: {
        Row: newOrigin.Row,
        Column: newOrigin.Column,
        Height: newCabinet.height,
        Width: newCabinet.width
      },
      id: newCabinet.id,
      productType: cabinetInfo.productType,
      touchPointDirection: newCabinet.touchPointDirection,
      Name: cornerCabinet ? `${boundingBox.touchPointDirection} void space`
        : cabinetInfo.name || `${boundingBox.touchPointDirection} no space`,
      spacerType: cabinetInfo.spacerType || null
    });
    if (cornerCabinet && boundingBox.touchPointDirection === DIRECTION.LEFT) {
      const voidSpacer = getVoidSpace(
        cornerCabinetType, newCabinetBox, direction,
        newCabinet.id, cabinetInfo.productType
      );
      if (voidSpacer) {
        updatedProducts.push(voidSpacer);
      }
    }

    updatedProducts = getShiftedProducts(updatedProducts, anchorSide);

    if (!products.length) {
      updateDesignProducts(host, designId, updatedProducts, cabinetInfo,
        metadataBaseUrl, newCabinet?.touchPointDirection);

    } else {
      updateDesignProducts(host, designId, updatedProducts, cabinetInfo, metadataBaseUrl);
    }
  };
}

export function useCartAddAccessories(designId) {
  const {
    updateDesignAccessories,
    cabinetVisualizer,
  } = useCabinetVisualizerStore();

  const host = useConfigService('global-custom-commerce:host');

  return function addAccessorytoCart(accessoryInfo) {
    const existingAccessories = selectDesignAccessories(cabinetVisualizer, designId);
    const sku = String(accessoryInfo?.itemId);

    // Find the existing accessory or create a new one
    const existingAccessoryIndex = existingAccessories?.findIndex((acc) => acc.Sku === sku) ?? -1;
    let updatedAccessories;

    if (existingAccessoryIndex !== -1) {
      // If accessory exists, increment quantity
      updatedAccessories = [...existingAccessories];
      updatedAccessories[existingAccessoryIndex].Quantity += 1;
    } else {
      // If accessory is new, create and add it to the array
      updatedAccessories = [
        ...existingAccessories,
        {
          Sku: sku,
          Quantity: 1
        }
      ];
    }

    updateDesignAccessories(host, designId, updatedAccessories, accessoryInfo);
  };
}

export function useCartRemoveAccessories(designId) {
  const {
    updateDesignAccessories,
    cabinetVisualizer,
  } = useCabinetVisualizerStore();

  const host = useConfigService('global-custom-commerce:host');

  return function removeAccessoryFromCart(itemId) {
    const existingAccessories = selectDesignAccessories(cabinetVisualizer, designId);

    // Find the existing accessory
    const existingAccessoryIndex = existingAccessories.findIndex((acc) => acc.Sku === itemId);

    if (existingAccessoryIndex !== -1) {
      // Remove the accessory
      const updatedAccessories = existingAccessories.filter((acc) => acc.Sku !== itemId);
      updateDesignAccessories(host, designId, updatedAccessories);
    }
  };
}
